import React, { useState } from 'react';
import { StickyNote } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import EpisodeNotes from './EpisodeNotes';

export interface EpisodeNotesPopoverProps {
  episodeId: string;
}

/**
 * The selected episode's shared notes, reachable from the patient header.
 *
 * The same log the Edit Episode dialog and each task's Notes dialog show. It
 * lives behind a popover rather than inline because the header is a dense
 * one-line strip that every patient screen sits under, and the log is
 * unbounded.
 *
 * Radix unmounts the content when the popover closes, so each open remounts
 * `EpisodeNotes` and refetches — a note added from a task detail is never
 * stale here.
 */
const EpisodeNotesPopover: React.FC<EpisodeNotesPopoverProps> = ({
  episodeId,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='h-6 gap-1 border-purple-200 bg-purple-50 px-2 text-xs font-medium text-purple-700 hover:bg-purple-100 hover:text-purple-800'
        >
          <StickyNote className='h-3.5 w-3.5' aria-hidden='true' />
          Episode notes
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-96'>
        <EpisodeNotes
          episodeId={episodeId}
          description='Shared by every task in this episode.'
        />
      </PopoverContent>
    </Popover>
  );
};

export default EpisodeNotesPopover;
